
import React, { useState, useEffect } from 'react';
import { Icons } from './Icons';

interface ConfirmationModalProps {
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmButtonText?: string;
    cancelButtonText?: string;
    isConfirming?: boolean;
    confirmationText?: string; // e.g., "delete my account"
    confirmationLabel?: string; // e.g., "To confirm, type..."
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    title,
    message,
    onConfirm,
    onCancel,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    isConfirming = false,
    confirmationText,
    confirmationLabel,
}) => {
    const [inputText, setInputText] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(!confirmationText);

    useEffect(() => {
        if (confirmationText) {
            setIsConfirmed(inputText === confirmationText);
        }
    }, [inputText, confirmationText]);


    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-black/80 transition-opacity z-50 flex justify-center items-center p-4" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="fixed inset-0" onClick={onCancel} aria-hidden="true"></div>
            <div className="relative bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                            <Icons.AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="modal-title">
                                {title}
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {message}
                                </p>
                            </div>
                        </div>
                    </div>
                     {confirmationText && (
                        <div className="mt-4">
                            <label htmlFor="confirmation-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                {confirmationLabel || `To confirm, type "${confirmationText}" below:`}
                            </label>
                            <input
                                type="text"
                                name="confirmation-input"
                                id="confirmation-input"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                className="mt-1 block w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                            />
                        </div>
                    )}
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-red-400 disabled:cursor-not-allowed"
                        onClick={onConfirm}
                        disabled={!isConfirmed || isConfirming}
                    >
                        {isConfirming ? (
                            <>
                                <Icons.Loader className="w-5 h-5 mr-2 animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            confirmButtonText
                        )}
                    </button>
                    <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={onCancel}
                        disabled={isConfirming}
                    >
                        {cancelButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
